import { Box, Center, Stack, Text } from '@mantine/core';
import { IconPhotoOff } from '@tabler/icons-react';
import { useEffect, useState } from 'react';

type AssetImageProps = {
  src: string | null | undefined;
  alt: string;
  /** CSS aspect-ratio, e.g. '7 / 12' for a card or '16 / 9' for a cover. */
  ratio?: string;
  objectPosition?: string;
  /**
   * `cover` заполняет рамку и обрезает лишнее — так выглядят карточки каталога,
   * у которых форма задана оформлением. `contain` показывает картинку целиком:
   * в администрировании важно увидеть саму обложку, какой бы формы она ни была,
   * а не подогнанный под рамку кусок (§бэклог 10).
   */
  fit?: 'cover' | 'contain';
  /** Shown when the file is missing or fails to load. */
  fallbackLabel?: string;
};

/**
 * Image with an explicit missing-asset state (§55).
 *
 * Artwork arrives incrementally, so a not-yet-delivered file must render as a
 * calm labelled placeholder rather than a broken-image icon.
 */
export function AssetImage({
  src,
  alt,
  ratio = '16 / 9',
  objectPosition = 'center',
  fit = 'cover',
  fallbackLabel = 'Изображение не загружено',
}: AssetImageProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  const showFallback = !src || failed;

  /*
    По своим пропорциям: рамки нет вовсе, высоту задаёт сама картинка.

    Так показывают то, что автор загрузил сам: форма у такой картинки любая, и
    рамка обрезала бы её по краям — а поставлена она была целиком. Заглушке при
    этом высота нужна, поэтому для неё рамка остаётся.
  */
  if (ratio === 'auto' && !showFallback) {
    return (
      <img
        src={src}
        alt={alt}
        onError={() => setFailed(true)}
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          borderRadius: 'var(--radius-card)',
        }}
      />
    );
  }

  /*
    Картинка растянута по рамке через `inset`, а не через `height: 100%`.

    Высота рамки задана только пропорцией, и процентная высота внутри неё ни к
    чему не приводится. Chrome догадывался, Safari — нет: он показывал картинку
    в натуральную величину, а рамка обрезала её по левому верхнему углу. На
    iPhone у Дарьи от обложек оставались фрагменты — это оно.
  */
  return (
    <Box
      style={{
        position: 'relative',
        aspectRatio: ratio === 'auto' ? '16 / 9' : ratio,
        overflow: 'hidden',
        background: 'var(--surface-sunken)',
      }}
    >
      {showFallback ? (
        <Center h="100%" p="sm">
          <Stack align="center" gap={6} c="var(--text-subtle)">
            <IconPhotoOff size={24} stroke={1.5} />
            <Text size="xs" ta="center">
              {fallbackLabel}
            </Text>
          </Stack>
        </Center>
      ) : (
        <img
          src={src}
          alt={alt}
          onError={() => setFailed(true)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: fit,
            objectPosition,
            display: 'block',
          }}
        />
      )}
    </Box>
  );
}
